// FLAG:useDocuAi — plan `review-booking-target` T6: when may the „Dieser Beleg ist eine Einnahme"
// checkbox be PRESELECTED?
//
// The default remains expense. Preselection requires HARD evidence, and hard evidence here means
// an identifier, not similarity.
//
// - **Tax-number match.** The analysis identified
// the tenant as the ISSUER through its tax number
// (`documentDirection === 'outgoing'` AND `documentDirectionMatch === 'taxNumber'`). If our own
// tax number appears for the invoice issuer, we issued the receipt ourselves — it is revenue.
// - **Self-billing.** UNTDID 389 / 261: the customer issues the document. If such a receipt is in
// our inbox, our customer created it for our service.
//
// Explicitly NOT preselected (user decision 2026-08-14, against the alternatives "never" and "also
// for name + postal code"): `documentDirectionMatch === 'nameAndZip'` or `'name'`. A namesake with the same
// postal code could produce a false preselection — and users are more likely to accept a wrong
// preselection than notice a missing one. The direction remains a hint in the contact section.
//
// The return value includes the REASON, not just the result: a preselected checkbox without an
// explanation is worse than none, so the UI must
// be able to say where the suggestion came from.
//
// What this module does NOT do: decide. It returns
// a suggestion that ranks behind the reviewer's
// own click AND the persisted decision in the host's precedence chain (`bookingTargetChoice ??
// persisted ?? evidence?.target`). It can therefore never overwrite a decision, and it can never
// suggest "expense" — expense is already the default, so such a suggestion would be meaningless.
import {
  getDocumentTypeCandidates,
  isSelfBilledDocumentType,
} from '@/modules/document/utils/document-invoice-kind'
import { BookingTarget } from '@/modules/receipt/review/review-target'

export type BookingTargetEvidenceReason = 'taxNumber' | 'selfBilled'

export interface BookingTargetEvidence {
  target: BookingTarget
  reason: BookingTargetEvidenceReason
}

/**
 * @param file       the loaded document (metaData.ocr, documentInvoiceDetails)
 * @param reviewData the review data (buildReviewData) — includes documentDirection /
 *                   documentDirectionMatch and the receipt type chosen by the reviewer
 * @returns `null` when there is no hard evidence — the normal case.
 */
export const resolveBookingTargetEvidence = (
  file: unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reviewData: Record<string, any> = {},
): BookingTargetEvidence | null => {
  // Order = strength of evidence. The tax number is the more conclusive finding: it identifies the
  // tenant, whereas the type code only describes the kind of receipt.
  if (reviewData?.documentDirection === 'outgoing' && reviewData?.documentDirectionMatch === 'taxNumber') {
    return { target: 'revenue', reason: 'taxNumber' }
  }

  if (getDocumentTypeCandidates(file, reviewData).some(isSelfBilledDocumentType)) {
    return { target: 'revenue', reason: 'selfBilled' }
  }

  return null
}
